/**
 * @file Client-side route table for `admin.labcraft.dev`.
 * @module @labcraft/admin/ui/router
 *
 * One always-rendered shell + a routed panel area. Routes:
 *
 *   - `/` — {@link AdminHomePage}. Anonymous admins see an inline
 *     `SignInCard` (Workspace SSO only); signed-in admins see the
 *     operator dashboard placeholder.
 *   - `/learners` — Learners panel. When the operator clicks a row,
 *     the `?l=<id>` query param opens the detail drawer inline; the
 *     route doesn't change.
 *   - `/learners/:id` — Full-page learner detail (alternative to
 *     drawer mode). Not used by default UI today; route reserved for
 *     the design's `peopleDetailMode === 'page'` tweak.
 *   - `/emails` — Emails sub-app compose surface. `/emails/campaigns`
 *     lists sent campaigns; `/emails/campaigns/:id` is the per-recipient
 *     delivery detail; `/emails/suppressions` is the opt-out list.
 *   - Anything else → redirects to `/` (the home / dashboard).
 *
 * Mounted inside `<ClientProvider>` in `main.tsx`. The shell always
 * renders chrome — anonymous admins get the
 * popover-trigger button in the TopNav and the inline SignInCard in
 * the page body. Once Google returns, the shell's
 * `auth.refresh()` finds the new session and the dashboard
 * renders without a full reload.
 */

import type { JSX } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router'
import { AdminHomePage } from './home/home_page.tsx'
import { AdminShell } from './kernel/admin_shell.tsx'
import { CoursesPanel } from './courses/courses_panel.tsx'
import { HypervisorPanel } from './hypervisor/hypervisor_panel.tsx'
import { LearnersPanel } from './learners/learners_panel.tsx'
import { EmailsLayout } from './emails/emails_layout.tsx'
import { ComposePanel } from './emails/compose_panel.tsx'
import { HistoryPanel } from './emails/history_panel.tsx'
import { CampaignsPanel } from './emails/campaigns_panel.tsx'
import { CampaignDetailPanel } from './emails/campaign_detail_panel.tsx'
import { SuppressionsPanel } from './emails/suppressions_panel.tsx'
import { RecalibrationPanel } from './recalibration/recalibration_panel.tsx'
import { SettingsPanel } from './settings/settings_panel.tsx'

/** Root router. Mounted inside `<ClientProvider>` in `main.tsx`. */
export function AppRouter(): JSX.Element {
  return (
    <BrowserRouter>
      <Routes>
        <Route element={<AdminShell />}>
          <Route path="/" element={<AdminHomePage />} />
          <Route path="/learners" element={<LearnersPanel />} />
          <Route path="/learners/:id" element={<LearnersPanel />} />
          <Route path="/courses" element={<CoursesPanel />} />
          <Route path="/emails" element={<EmailsLayout />}>
            <Route index element={<ComposePanel />} />
            <Route path="history" element={<HistoryPanel />} />
            <Route path="campaigns" element={<CampaignsPanel />} />
            <Route path="campaigns/:id" element={<CampaignDetailPanel />} />
            <Route path="suppressions" element={<SuppressionsPanel />} />
          </Route>
          <Route path="/recalibration" element={<RecalibrationPanel />} />
          <Route path="/hypervisors" element={<HypervisorPanel />} />
          <Route path="/settings" element={<SettingsPanel />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  )
}
